import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { addSeconds, isAfter } from "date-fns";

import { WatchedDate } from "@/stores/listeners/timeline-listeners/create-date-listener";
import { getCurrentDate } from "@/utils/datetime";

/**
 * A date information object for Socratic dialogue timing.
 */
export type SocraticState = {
  /**
   * Timing state for the current Dialogue
   * On reattempt, this is reset for the new Dialogue
   */
  dialogueTimeline: {
    /**
     * ID of this dialogue.
     */
    dialogueId: string | null;
    /**
     * Date when the dialogue was started.
     */
    dialogueStartDate: WatchedDate;
    /**
     * Date when the preparation time ends.
     * Student has preparation time from dialogue.startedAt + preparationTime.
     * If student starts recording early, this becomes the actual recording start time.
     */
    preparationEndDate: WatchedDate;
    /**
     * Date when the response/recording time ends.
     * This is calculated from when recording actually starts (either at preparation end or early start)
     * + responseTime.
     */
    responseEndDate: WatchedDate;
    /**
     * Whether the student has started recording.
     * This can happen during preparation time (early start) or after preparation time ends.
     */
    isRecording: boolean;
    /**
     * Whether recording has completed (either manually stopped or time expired).
     */
    recordingComplete: boolean;
    /**
     * The configured response time in seconds.
     * Stored so we can recalculate responseEndDate if student starts early.
     */
    responseTimeSeconds: number | null;
  };

  /**
   * Date when the reattempt window ends
   */
  reattemptWindowEndDate: WatchedDate;
  /**
   * How long the reattempt window is, as it applies to this student. Null
   * whenever no window applies. Held so the window end can be counted again
   * from the moment the student stops recording.
   */
  reattemptWindowSeconds: number | null;
  /**
   * Latest date/time a recording is allowed to start and still complete
   * before assessment auto-submission.
   */
  recordingStartCutoffDate: WatchedDate;
  /**
   * When true, the camera-required constraint is disabled and students may
   * turn off their camera. Will be driven by assessment settings in future.
   */
  cameraRequiredDisabled: boolean;
  /**
   * Whether a Socratic video upload is currently in progress.
   * Used to gate auto-submission navigation until the upload completes.
   */
  isUploading: boolean;
};

const mkDateState = (): WatchedDate => ({ date: null, passed: false });

const initialState: SocraticState = {
  dialogueTimeline: {
    dialogueId: null,
    dialogueStartDate: mkDateState(),
    preparationEndDate: mkDateState(),
    responseEndDate: mkDateState(),
    isRecording: false,
    recordingComplete: false,
    responseTimeSeconds: null,
  },
  reattemptWindowEndDate: mkDateState(),
  reattemptWindowSeconds: null,
  recordingStartCutoffDate: mkDateState(),
  cameraRequiredDisabled: false,
  isUploading: false,
};

export const socraticSlice = createSlice({
  name: "socratic",
  initialState,
  reducers: {
    /**
     * Initialise the Socratic Dialogue timing state. Every date arrives already
     * resolved (see `resolveDialogueTimeline`) so that the page and the slice
     * cannot disagree about where the attempt stands.
     */
    updateSocraticDialogueTimeline: (
      { dialogueTimeline },
      action: PayloadAction<{
        dialogueId: string;
        /** ISO. */
        startedAt: string;
        /** ISO. The recording start on a resume. */
        preparationEndDate: string;
        /** ISO. Already capped at the dialogue's expiry. */
        responseEndDate: string;
        responseTimeSeconds: number;
        /** Whether recording is already underway — a resume. */
        isRecording: boolean;
      }>
    ) => {
      const {
        dialogueId,
        startedAt,
        preparationEndDate,
        responseEndDate,
        responseTimeSeconds,
        isRecording,
      } = action.payload;
      const now = getCurrentDate();

      dialogueTimeline.dialogueId = dialogueId;
      dialogueTimeline.responseTimeSeconds = responseTimeSeconds;

      setSocraticDate(dialogueTimeline.dialogueStartDate, startedAt, now);
      setSocraticDate(
        dialogueTimeline.preparationEndDate,
        preparationEndDate,
        now
      );
      setSocraticDate(dialogueTimeline.responseEndDate, responseEndDate, now);

      if (isRecording) {
        // `setSocraticDate` compares strictly, so a recording start equal to or
        // ahead of now leaves this false. The date listener would then fire
        // `preparationEndDatePassed` and measure a new response window from now.
        dialogueTimeline.preparationEndDate.passed = true;
      }

      const responseWindowOver =
        dialogueTimeline.responseEndDate.passed === true;
      dialogueTimeline.isRecording = isRecording && !responseWindowOver;
      dialogueTimeline.recordingComplete = responseWindowOver;
    },

    /**
     * Mark that preparation time has passed.
     * If recording hasn't started yet, automatically start recording now.
     */
    preparationEndDatePassed: ({ dialogueTimeline }) => {
      dialogueTimeline.preparationEndDate.passed = true;

      // If not already recording, automatically start recording now
      if (
        !dialogueTimeline.isRecording &&
        !dialogueTimeline.recordingComplete
      ) {
        dialogueTimeline.isRecording = true;

        // Recalculate response end date from now, but never later than the
        // deadline already on record. Otherwise a student who reloaded into a
        // part-spent window would be handed a full one.
        if (dialogueTimeline.responseTimeSeconds !== null) {
          const now = getCurrentDate();
          const fromNow = addSeconds(now, dialogueTimeline.responseTimeSeconds);
          const existing = dialogueTimeline.responseEndDate.date;
          const responseEndDate =
            existing != null && isAfter(fromNow, new Date(existing))
              ? new Date(existing)
              : fromNow;
          setSocraticDate(
            dialogueTimeline.responseEndDate,
            responseEndDate,
            now
          );
        }
      }
    },

    /**
     * Mark that response time has passed.
     * Recording automatically stops.
     */
    responseEndDatePassed: (state) => {
      finishRecording(state, getCurrentDate());
      state.dialogueTimeline.responseEndDate.passed = true;
    },

    /**
     * Student has chosen to start recording early (during preparation time).
     * Recalculates response end date from the early start time.
     * Marks preparation time as passed since we're skipping the rest of it.
     */
    startRecordingEarly: ({ dialogueTimeline }) => {
      if (
        !dialogueTimeline.isRecording &&
        !dialogueTimeline.recordingComplete
      ) {
        dialogueTimeline.isRecording = true;

        const now = getCurrentDate();

        setSocraticDate(dialogueTimeline.preparationEndDate, now, now); // Mark preparation end as now (passed)

        // Recalculate response end date from now (early start)
        if (dialogueTimeline.responseTimeSeconds !== null) {
          const responseEndDate = addSeconds(
            now,
            dialogueTimeline.responseTimeSeconds
          );
          setSocraticDate(
            dialogueTimeline.responseEndDate,
            responseEndDate,
            now
          );
        }
      }
    },

    /**
     * Student has chosen to stop recording early (before response time ends).
     * Marks response time as passed since we're done recording.
     */
    stopRecordingEarly: (state) => {
      if (!state.dialogueTimeline.isRecording) return;

      const now = getCurrentDate();

      setSocraticDate(state.dialogueTimeline.responseEndDate, now, now);
      finishRecording(state, now);
    },

    /**
     * Reset the Socratic dialogue state.
     */
    resetSocraticDialogue: (state) => {
      state.dialogueTimeline = initialState.dialogueTimeline;
    },

    /**
     * Initialise the Socratic Session timing state.
     */
    updateSocraticSessionTimeline: (
      state,
      action: PayloadAction<{
        /**
         * ISO. The latest recording the student finished anywhere in this
         * session, which the reattempt window is counted from.
         */
        latestRecordingCompletedAt: string | null;
        reattemptWindowSeconds: number | null;
        /**
         * ISO. The anchor before any recording exists is the attempt's expiry,
         * which the client does not compute — so this is the only source then.
         */
        windowEndBeforeAnyRecording: string | null;
        /** ISO. */
        recordingStartCutoffAt: string | null;
      }>
    ) => {
      const {
        latestRecordingCompletedAt,
        reattemptWindowSeconds,
        windowEndBeforeAnyRecording,
        recordingStartCutoffAt,
      } = action.payload;
      const now = getCurrentDate();

      state.reattemptWindowSeconds = reattemptWindowSeconds;

      const windowEnd = resolveWindowEnd({
        latestRecordingCompletedAt,
        reattemptWindowSeconds,
        windowEndBeforeAnyRecording,
      });

      setSocraticDate(state.reattemptWindowEndDate, windowEnd, now);
      setSocraticDate(
        state.recordingStartCutoffDate,
        recordingStartCutoffAt,
        now
      );
    },

    /**
     * Mark that the reattempt window has passed.
     */
    reattemptWindowEndDatePassed: (state) => {
      state.reattemptWindowEndDate.passed = true;
    },

    /**
     * Mark that the recording start cutoff has passed.
     */
    recordingStartCutoffDatePassed: (state) => {
      state.recordingStartCutoffDate.passed = true;
    },

    setCameraRequiredDisabled: (state, action: PayloadAction<boolean>) => {
      state.cameraRequiredDisabled = action.payload;
    },

    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
  },
});

/**
 * The recording has stopped, whichever way. Does nothing the second time: an
 * early stop sets `responseEndDate` to now, which the response-end listener
 * answers with `responseEndDatePassed` milliseconds later.
 */
function finishRecording(state: Draft<SocraticState>, now: Date) {
  const { dialogueTimeline } = state;
  if (dialogueTimeline.recordingComplete) return;

  dialogueTimeline.isRecording = false;
  dialogueTimeline.recordingComplete = true;

  const seconds = state.reattemptWindowSeconds;
  setSocraticDate(
    state.reattemptWindowEndDate,
    seconds == null ? null : addSeconds(now, seconds),
    now
  );
}

/**
 * Where the window ends: counted from the student's own last recording once
 * one exists, and taken from the server before that.
 */
function resolveWindowEnd({
  latestRecordingCompletedAt,
  reattemptWindowSeconds,
  windowEndBeforeAnyRecording,
}: {
  latestRecordingCompletedAt: string | null;
  reattemptWindowSeconds: number | null;
  windowEndBeforeAnyRecording: string | null;
}): string | Date | null {
  if (latestRecordingCompletedAt == null) return windowEndBeforeAnyRecording;
  if (reattemptWindowSeconds == null) return null;

  return addSeconds(
    new Date(latestRecordingCompletedAt),
    reattemptWindowSeconds
  );
}

function setSocraticDate(
  draft: Draft<WatchedDate>,
  value: string | Date | null,
  now: Date
) {
  if (value != null) {
    const date = new Date(value);
    draft.date = date.toISOString();
    draft.passed = isAfter(now, date);
  } else {
    draft.date = null;
    draft.passed = false;
  }
}

export const {
  updateSocraticDialogueTimeline,
  preparationEndDatePassed,
  responseEndDatePassed,
  startRecordingEarly,
  stopRecordingEarly,
  resetSocraticDialogue,
  updateSocraticSessionTimeline,
  reattemptWindowEndDatePassed,
  recordingStartCutoffDatePassed,
  setCameraRequiredDisabled,
  setIsUploading,
} = socraticSlice.actions;

export const socraticReducer = socraticSlice.reducer;
